import { Formik, FormikProps } from "formik";
import { useCallback, useEffect } from "react";
import { IAddPlanFormValues, addPlanValidationSchema, IDistributorPricing } from "../../../../../common/validations/add-plan.validations";
import { IDistributor } from "./add-distributor-dropdown";
import { ModuleData } from "./modules-table/modules.mock-data";

interface AddPlanFormHandlers {
  handleDistributorAdd: (distributor: IDistributor) => void;
  handleDistributorDelete: (distributorId: string) => void;
  handleModuleChange: (moduleData: ModuleData) => void;
  handleDistributorPricingChange: (distributorId: string, pricing: Partial<IDistributorPricing>) => void;
}

export interface AddPlanFormChildrenProps extends FormikProps<IAddPlanFormValues>, AddPlanFormHandlers {}

interface AddPlanFormWrapperProps {
  initialValues: IAddPlanFormValues;
  onFormStateChange?: (values: IAddPlanFormValues, isValid: boolean) => void;
  onSubmit: (values: IAddPlanFormValues) => void;
  children: (props: AddPlanFormChildrenProps) => React.ReactNode;
}

interface FormContentProps {
  formikProps: FormikProps<IAddPlanFormValues>;
  onFormStateChange?: (values: IAddPlanFormValues, isValid: boolean) => void;
  onSubmit: (values: IAddPlanFormValues) => void;
  children: (props: AddPlanFormChildrenProps) => React.ReactNode;
}

const FormContent = ({ formikProps, onFormStateChange, onSubmit, children }: FormContentProps) => {
  const { values, isValid, setFieldValue } = formikProps;

  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(values, isValid);
    }
  }, [values, isValid, onFormStateChange]);

  const handleDistributorAdd = useCallback((distributor: IDistributor) => {
    const newDistributorPricing: IDistributorPricing = {
      distributor,
      currency: "GBP",
      costPeriod: "/month",
      costValue: "349.99",
      marginPercent: "30",
      pricePeriod: "/month",
    };
    setFieldValue("distributors", [...values.distributors, newDistributorPricing]);
  }, [values.distributors, setFieldValue]);

  const handleDistributorDelete = useCallback((distributorId: string) => {
    const updatedDistributors = values.distributors.filter(
      (d) => d.distributor.id !== distributorId
    );
    setFieldValue("distributors", updatedDistributors);
  }, [values.distributors, setFieldValue]);

  const handleModuleChange = useCallback((moduleData: ModuleData) => {
    const moduleIndex = values.modules.findIndex((m) => m.label === moduleData.label);
    if (moduleIndex !== -1) {
      setFieldValue(`modules.${moduleIndex}`, moduleData);
    }
  }, [values.modules, setFieldValue]);

  const handleDistributorPricingChange = useCallback(
    (distributorId: string, pricing: Partial<IDistributorPricing>) => {
      const distributorIndex = values.distributors.findIndex(
        (d) => d.distributor.id === distributorId
      );
      if (distributorIndex !== -1) {
        const updatedDistributor = {
          ...values.distributors[distributorIndex],
          ...pricing,
        };
        setFieldValue(`distributors.${distributorIndex}`, updatedDistributor);
      }
    },
    [values.distributors, setFieldValue]
  );

  return (
    <>
      {children({
        ...formikProps,
        handleDistributorAdd,
        handleDistributorDelete,
        handleModuleChange,
        handleDistributorPricingChange,
        handleSubmit: (e) => {
          e?.preventDefault();
          onSubmit(values);
        },
      })}
    </>
  );
};

const AddPlanFormWrapper = ({
  initialValues,
  onFormStateChange,
  onSubmit,
  children,
}: AddPlanFormWrapperProps) => {
  const handleSubmit = (values: IAddPlanFormValues) => {
    onSubmit(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={addPlanValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validateOnMount
    >
      {(formikProps) => (
        <FormContent
          formikProps={formikProps}
          onFormStateChange={onFormStateChange}
          onSubmit={onSubmit}
          children={children}
        />
      )}
    </Formik>
  );
};

export default AddPlanFormWrapper;

